/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@ant-design/icons';

/**
 * Renders a box-icon with customizable properties. The icon is rendered using the boxicons library.
 *
 * @component
 * @param {string} name - The name of the icon. The full list of icons can be found at https://boxicons.com/.
 * @param {string} type - The type of the icon ('solid', 'logo', 'regular').
 * @param {string} size - The size of the icon.
 * @param {number} rotate - The rotation angle of the icon.
 * @param {string} flip - The flip direction of the icon.
 * @param {string} border - The border style of the icon.
 * @param {string} animation - The animation style of the icon.
 * @param {boolean} animateOnHover - Whether to show the animation only on hover.
 * @param {string} className - Additional CSS class for the icon.
 * @returns {JSX.Element} The rendered BoxIcon component.
 */
const BoxIcon = ({
  name,
  type,
  size,
  rotate,
  flip,
  border,
  animation,
  animateOnHover = false,
  className,
  ...props
}) => {
  const baseClass = 'bx';
  const nameClass = `${type === 'solid' ? 'bxs' : type === 'logo' ? 'bxl' : 'bx'}-${name}`;
  const sizeClass = `bx-${size}`;
  const rotateClass = rotate ? `bx-rotate-${rotate}` : '';
  const flipClass = flip ? `bx-flip-${flip}` : '';
  const borderClass = border && border === 'square' ? 'bx-border' : `bx-border-${border}`;
  const animationClass = animation && animateOnHover ? `bx-${animation}-hover` : animation ? `bx-${animation}` : '';
  const customClass = className || '';

  const fullClassName = [baseClass, nameClass, sizeClass, rotateClass, flipClass, borderClass, animationClass, customClass].filter(Boolean).join(' ');

  return (
    <Icon
      component={() => <i className={fullClassName} ></i>}
      {...props}
    />
  );
};

BoxIcon.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['solid', 'regular', 'logo']),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // xs|sm|md|lg
  rotate: PropTypes.oneOf([90, 180, 270]),
  flip: PropTypes.oneOf(['horizontal', 'vertical']),
  border: PropTypes.oneOf(['square', 'circle']),
  animation: PropTypes.string, // spin|tada|flashing|burst|fade-left|fade-right
  animateOnHover: PropTypes.bool, // whether to show the animation only on hover
  className: PropTypes.string
};

export default BoxIcon;
